import React from "react";
import { LangLink as Link } from "@/components/LangLink";
import SEO from "@/components/SEO";
import PageHeader from "@/components/PageHeader";
import FinalCTA from "@/components/FinalCTA";
import { CheckCircle2 } from "lucide-react";
import { useT } from "@/hooks/useT";

export default function Cmimet() {
  const t = useT();

  const PLANS = [
    {
      name: "Starter",
      desc: t("Për apartamente dhe guest house të vogla", "For small apartments and guest houses"),
      price: "~",
      suffix: t("/muaj", "/mo"),
      note: t("deri në 10 dhoma", "up to 10 rooms"),
      features: [
        t("PMS me kalendar rezervimesh",           "PMS with booking calendar"),
        t("Booking engine standard",                "Standard booking engine"),
        t("Lidhje me 2 kanale",                     "Connection to 2 channels"),
        t("Moduli i pastrimit",                     "Housekeeping module"),
        t("Raporte bazë",                           "Basic reports"),
        t("Mbështetje me email",                    "Email support"),
      ],
      cta: t("Filloni me Starter", "Start with Starter"),
      highlight: false,
    },
    {
      name: "Professional",
      desc: t("Për hotele dhe boutique me ekip të dedikuar", "For hotels and boutiques with a dedicated team"),
      price: "~",
      suffix: t("/muaj", "/mo"),
      note: t("deri në 40 dhoma", "up to 40 rooms"),
      features: [
        t("Të gjitha të Starter",                       "Everything in Starter"),
        t("Channel Manager (të gjitha kanalet)",         "Channel Manager (all channels)"),
        t("Booking engine i personalizueshëm",           "Customisable booking engine"),
        t("POS i integruar",                              "Integrated POS"),
        t("Check-in online",                              "Online check-in"),
        t("Mbështetje 12/7 në shqip",                    "12/7 multilingual support"),
      ],
      cta: t("Provoni Professional", "Try Professional"),
      highlight: true,
    },
    {
      name: "Enterprise",
      desc: t("Për resorte dhe pronarë me disa objekte", "For resorts and owners with multiple properties"),
      price: t("Të dakordojmë", "Let's talk"),
      suffix: "",
      note: t("pa kufi dhomash", "unlimited rooms"),
      features: [
        t("Të gjitha të Professional",                       "Everything in Professional"),
        t("Menaxhim për disa objekte",                        "Multi-property management"),
        t("Çmimet dhe të Ardhurat të avancuara",             "Advanced pricing & revenue"),
        t("API e dedikuar dhe integrime të personalizuara",   "Dedicated API & custom integrations"),
        t("Llogari menaxheri i dedikuar",                     "Dedicated account manager"),
        t("Mbështetje 24/7 në shqip",                        "24/7 multilingual support"),
      ],
      cta: t("Bisedoni me ne", "Talk to us"),
      highlight: false,
    },
  ];

  const FAQS = [
    [
      t("A ka kosto në fillim?", "Are there any upfront costs?"),
      t(
        "Jo. Filloni të paguani vetëm kur jeni gati të punoni. Konfigurimi dhe trajnimi përfshihen në abonim.",
        "No. You only start paying once you're ready to go live. Setup and training are included in your subscription."
      ),
    ],
    [
      t("A mund të ndryshoj planin më vonë?", "Can I change my plan later?"),
      t(
        "Po. Mund të kaloni në një plan më të lartë ose më të ulët në çdo kohë.",
        "Yes. You can upgrade or downgrade at any time."
      ),
    ],
    [
      t("A funksionon me sistemin fiskal shqiptar?", "Does it work with the Albanian fiscal system?"),
      t(
        "Po. Akomoda lidhet me sistemin e fiskalizimit në Shqipëri.",
        "Yes. Akomoda is integrated with Albania's fiscalization system."
      ),
    ],
    [
      t("A bën punë për apartamente me qera?", "Does it work for short-term rentals?"),
      t(
        "Po. Sistemi funksionon edhe për apartamente turistike dhe pronarë Vrbo.",
        "Yes. The system also works for holiday apartments and Vrbo hosts."
      ),
    ],
  ];

  const dakordText = t("Të dakordojmë", "Let's talk");

  const schemas = [
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map(([q, a]) => ({
        "@type": "Question",
        name: q,
        acceptedAnswer: { "@type": "Answer", text: a },
      })),
    },
    ...PLANS.filter((p) => p.price !== dakordText && p.price !== "~").map((p) => ({
      "@context": "https://schema.org",
      "@type": "Product",
      name: `Akomoda ${p.name}`,
      description: p.desc,
      brand: { "@type": "Brand", name: "Akomoda" },
      offers: {
        "@type": "Offer",
        price: p.price,
        priceCurrency: "EUR",
        url: "https://akomoda.co/cmimet",
        availability: "https://schema.org/InStock",
      },
    })),
  ];

  return (
    <>
      <SEO
        title={t("Çmimet — Plane për Hotele dhe Apartamente", "Pricing — Plans for Hotels & Apartments")}
        description={t(
          "Çmime të qarta për Akomoda: plane për apartamente, hotele dhe resorte në Shqipëri dhe Ballkan. Pa kontrata afatgjata.",
          "Clear Akomoda pricing: plans for apartments, hotels and resorts across Albania and the Balkans. No long-term contracts."
        )}
        schema={schemas}
      />
      <PageHeader
        eyebrow={t("Çmimet", "Pricing")}
        title={t("Çmime të qarta për çdo madhësi prone", "Clear pricing for every property size")}
        description={t(
          "Pa kontrata afatgjata. Pa kosto fillestare të fshehura. Filloni me një demo falas dhe shihni nëse ju përshtatet.",
          "No long-term contracts. No hidden upfront costs. Start with a free demo and see if it's the right fit."
        )}
        ctas={[
          { to: "/demo",    label: t("Kërko Demo", "Request Demo") },
          { to: "/kontakt", label: t("Pyetni për Çmim", "Ask about pricing"), variant: "ghost" },
        ]}
        breadcrumb={[{ label: t("Çmimet", "Pricing") }]}
      />

      <section className="bg-surface-1">
        <div className="container-akm grid gap-6 py-14 md:grid-cols-3 md:py-20">
          {PLANS.map((p) => (
            <div
              key={p.name}
              className={`rounded-xl border bg-white p-7 shadow-card ${p.highlight ? "border-[#FF7A00] ring-1 ring-[#FF7A00]/20" : "border-line"}`}
              data-testid={`plan-${p.name.toLowerCase()}`}
            >
              {p.highlight && (
                <div className="mb-3 inline-flex rounded-full bg-[#FF7A00] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-white">
                  {t("Më e popullarizuara", "Most popular")}
                </div>
              )}
              <div className="font-display text-xl font-extrabold text-ink">{p.name}</div>
              <div className="mt-1 text-sm text-slate2">{p.desc}</div>
              <div className="mt-5 flex items-baseline gap-1">
                {p.price !== "Personalizuar" && p.price !== "~" && p.price !== dakordText && (
                  <span className="font-display text-xl font-bold text-slate2">€</span>
                )}
                <span className="font-display text-4xl font-extrabold tracking-tight text-ink">{p.price}</span>
                {p.suffix && p.price !== "~" && <span className="text-sm text-slate2">{p.suffix}</span>}
              </div>
              <div className="mt-1 text-xs text-slate2">{p.note}</div>
              <ul className="mt-6 space-y-2 text-sm text-ink">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-[#FF7A00]" /> {f}
                  </li>
                ))}
              </ul>
              <Link
                to="/demo"
                className={`mt-7 inline-flex w-full justify-center rounded-md px-4 py-2.5 text-sm font-semibold transition-colors ${
                  p.highlight ? "bg-[#FF7A00] text-white hover:bg-[#EA580C]" : "border border-line bg-white text-ink hover:border-[#FF7A00] hover:text-[#FF7A00]"
                }`}
              >
                {p.cta}
              </Link>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-surface-2">
        <div className="container-akm py-14 md:py-20">
          <h2 className="h2-akm">{t("Pyetje të shpeshta", "Frequently asked questions")}</h2>
          <div className="mt-6 grid gap-6 md:grid-cols-2">
            {FAQS.map(([q, a]) => (
              <div key={q} className="card-akm">
                <div className="font-display text-base font-bold text-ink">{q}</div>
                <p className="mt-2 text-sm text-slate2">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA />
    </>
  );
}
